import React from 'react';
import { IconButton, Tooltip, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AppContext } from '../../../App';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogContentText from '@mui/material/DialogContentText';
import { PackageStatuses } from '../../../types/packager-types';

import { usePackageTriggerDelivery } from '../../hooks/packager/usePackageTriggerDelivery';

interface PackageSendProps {
  packagerId: string;
  packageId: string;
  packageStatus: PackageStatuses;
}

const PackageSend: React.FC<PackageSendProps> = ({ packagerId, packageId, packageStatus }) => {
  const { setToast } = React.useContext(AppContext);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const mutationPackagerUpdate = usePackageTriggerDelivery({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });
      queryClient.invalidateQueries({
        queryKey: ['packages']
      });
      // The detail query gates entry edits in PackagerDocuments.
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package sent successfully!',
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Failed to send package: ${error.message}`,
      });
    },
  });

  const handleManualDelivery = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const handleConfirmSend = () => {
    console.log('Manual delivery triggered for package:', packageId);

    mutationPackagerUpdate.mutate({
      packagerId: packagerId,
      packageId: packageId,
    });
  };

  return (
    <>
      <Tooltip title="Click to send delivery manually" placement="top">
        <span>
          <IconButton
            onClick={handleManualDelivery}
            size="small"
            disabled={
              mutationPackagerUpdate.isPending || packageStatus !== PackageStatuses.VERIFIED
            }
            sx={{
              color: theme.palette.custom.lightGreen,
              '&:hover': {
                backgroundColor: theme.palette.custom.lightGreen + '20', // 20% opacity for hover
              },
            }}
          >
            <SendIcon fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>

      <DialogGlynt
        open={open}
        closeModal={closeModal}
        width="400px"
        cypressId="dialog-package-send-confirmation"
      >
        <DialogTitleGlynt title="Confirm Package Delivery" closeModal={closeModal} />
        <DialogContentGlynt>
          <DialogContentText>Are you sure you want to send this package now?</DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleConfirmSend}
          closeModal={closeModal}
          cancelCypressId="btn-dialog-send-cancel"
          submitCypressId="btn-dialog-send-confirm"
          clearCypressId="btn-dialog-send-clear"
          submitButtonText="Send Package"
          submitLoading={mutationPackagerUpdate.isPending}
          showCancelButton={true}
          showClearButton={false}
          submitDisabled={mutationPackagerUpdate.isPending}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageSend;
